import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { unopenedStock } from '../../shared/fifo';
import type { ItemRow, LotRow, PurchaseRow } from '../../shared/types';
import { db, type QueueOp } from '../lib/db';

/** ホームのカード 1 枚に必要な導出値（SPEC §4.3） */
export interface ItemView {
  item: ItemRow;
  /** 未開封在庫数。在庫0での開封を許しているため負にもなりうる */
  stock: number;
  /** 使用中ロット（finished_at IS NULL）。開封が古い順 */
  openLots: LotRow[];
  /** 購入に紐付いていない使用中ロットがある（SPEC §4.4 のリカバリ経路） */
  hasUnlinkedLot: boolean;
  purchaseCount: number;
}

export interface AppData {
  loaded: boolean;
  views: ItemView[];
}

export function useAppData(): AppData {
  const raw = useLiveQuery(async () => {
    const [items, purchases, lots] = await Promise.all([
      db.items.toArray(),
      db.purchases.toArray(),
      db.lots.toArray(),
    ]);
    return { items, purchases, lots };
  }, []);

  return useMemo(() => {
    if (!raw) return { loaded: false, views: [] };
    return { loaded: true, views: buildViews(raw.items, raw.purchases, raw.lots) };
  }, [raw]);
}

function buildViews(items: ItemRow[], purchases: PurchaseRow[], lots: LotRow[]): ItemView[] {
  const sorted = items
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order || cmp(a.created_at, b.created_at));

  return sorted.map((item) => {
    const openLots = lots
      .filter((l) => l.item_id === item.id && l.finished_at === null)
      .sort((a, b) => cmp(a.opened_at, b.opened_at) || cmp(a.created_at, b.created_at));

    return {
      item,
      stock: unopenedStock(purchases, lots, item.id),
      openLots,
      hasUnlinkedLot: openLots.some((l) => l.purchase_id === null),
      purchaseCount: purchases.filter((p) => p.item_id === item.id).length,
    };
  });
}

/* ------------------------------------------------------------------ */

export interface SyncStatus {
  pending: number;
  failed: QueueOp[];
}

export function useSyncStatus(): SyncStatus {
  const ops = useLiveQuery(() => db.syncQueue.orderBy('seq').toArray(), []) ?? [];
  return useMemo(
    () => ({
      pending: ops.filter((o) => o.status === 'pending').length,
      failed: ops.filter((o) => o.status === 'failed'),
    }),
    [ops],
  );
}

/** ブラウザのオンライン状態。オフライン表示の出し分けに使う。 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
